import { Api, ApiError } from "../api";
import { navigate } from "../nav";
import { h, replace } from "../dom";
import type { ActResult, Me, Mode } from "../types";
import { plain } from "../words";

/** Whether the signed-in person holds a permission (docs/design/permissions.md); a button shows only if they do. */
export function can(me: Me, permission: string): boolean {
  return me.permissions?.includes(permission) ?? false;
}

/** One step of a breadcrumb: a link, or the current page (no href). `num` sets it in figures (PO numbers). */
export interface Crumb { text: string; href?: string; num?: boolean }

/** The trail to this screen: "Orders · LaserTech Metal Works · 14033 · TG-S0001 · Email". */
export function crumbs(...steps: (Crumb | null | false | undefined)[]): HTMLElement {
  const parts = steps.filter((s): s is Crumb => !!s).flatMap((s, i) => {
    const el = s.href ? h("a", { href: s.href, class: s.num ? "num" : null }, s.text) : h("span", { class: s.num ? "num" : null }, s.text);
    return i ? [" · ", el] : [el];
  });
  return h("nav", { "aria-label": "Breadcrumb", class: "crumbs" }, parts);
}

/** The trail down to an order: Orders, its customer, its PO number. */
export function orderCrumbs(customerPoId: number, poNumber: string, customer?: string | null): Crumb[] {
  return [{ text: "Orders", href: "/orders" }, ...(customer ? [{ text: customer }] : []),
    { text: poNumber, href: `/order/${customerPoId}`, num: true }];
}

/** Said wherever a screen shows the other mode's rows than the platform runs in. */
export function modeNotice(showing: Mode, platform: Mode): HTMLElement {
  return h("div", { class: "notice" }, `Showing ${showing === "SIMULATION" ? "simulation copies" : "live rows"}; the platform is ` +
    `${platform}. Look freely; acts here are refused, since they follow the platform's mode.`);
}

/** A checkbox with its words beside it (a filter such as "Dormant and ended too"), lined up with the fields around it. */
export function checkField(id: string, text: string, checked: boolean): { field: HTMLElement; box: HTMLInputElement } {
  const box = h("input", { id, type: "checkbox", checked });
  return { field: h("label", { class: "check", for: id }, box, h("span", null, text)), box };
}

/** A button that shows (and hides) a form below it. */
export function toggle(text: string, body: () => HTMLElement): HTMLElement {
  const box = h("div", { hidden: true });
  const b = h("button", { type: "button", class: "button button-small", "aria-expanded": "false" }, text);
  b.addEventListener("click", () => {
    const open = box.hidden;
    if (open && !box.firstChild) box.appendChild(body());
    box.hidden = !open;
    b.setAttribute("aria-expanded", String(open));
  });
  return h("div", { class: "stack-tight" }, h("div", null, b), box);
}

/** How long a pressed button waits for its second press before it goes back. */
export const ARM_MS = 5000;

/**
 * A button for an act that's hard to undo: the first press arms it ("Press again to …") for ARM_MS, and only a second
 * press within that time runs it. No pop-up, which people learn to click through. `check` runs before arming (e.g. a
 * reason that must be written first); returning false keeps it from arming.
 */
export function confirmButton(text: string, again: string, run: () => void, cls = "", check?: () => boolean): HTMLButtonElement {
  const b = h("button", { type: "button", class: `button ${cls}` }, text);
  let timer: number | undefined;
  const disarm = () => {
    window.clearTimeout(timer);
    timer = undefined;
    b.textContent = text;
    b.classList.remove("armed");
  };
  b.addEventListener("click", () => {
    if (timer !== undefined) {
      disarm();
      run();
      return;
    }
    if (check && !check()) return;
    b.textContent = again;
    b.classList.add("armed");
    timer = window.setTimeout(disarm, ARM_MS);
  });
  b.addEventListener("blur", disarm);
  return b;
}

/**
 * What an act did, in a line. `done` is the app's own words for success (the tool's note is written for Claude, so
 * it isn't shown); a refusal shows the platform's reason in plain words.
 */
export function outcome(r: ActResult, done: string): HTMLElement {
  return r.status === "APPLIED"
    ? h("p", { class: "ok" }, done)
    : h("p", { class: "error" }, `Not done: ${plain(r.reason ?? r.message) || r.status.toLowerCase()}.`.replace(/\.\.$/, "."));
}

/**
 * "Prepare the email again" (prepare_po_email, drafts.write): an approved vendor PO that hasn't gone and has no email
 * waiting gets a fresh one; on success the app opens it. One press: it only makes a draft, and sending still takes Send.
 */
export function prepareEmailButton(api: Api, vendorPoId: number, report: HTMLElement, cls = ""): HTMLButtonElement {
  const b = h("button", { type: "button", class: `button ${cls}` }, "Prepare the email again");
  b.addEventListener("click", async () => {
    b.disabled = true;
    try {
      const r = await api.tool<ActResult & { emailDraft?: { draftId?: number } }>("prepare_po_email", { vendor_po_id: vendorPoId });
      if (r.status === "APPLIED" && r.emailDraft?.draftId) {
        navigate(`/draft/${r.emailDraft.draftId}`);
        return;
      }
      replace(report, outcome(r, "Prepared."));
    } catch (err) {
      replace(report, h("p", { class: "error" }, plain((err as ApiError).message)));
    }
    b.disabled = false;
  });
  return b;
}

/**
 * Opens a vendor PO's PDF in a new tab (a link valid 15 minutes). The tab opens on the click, before the link is
 * made, so the browser doesn't block it.
 */
export async function openPdf(api: Api, args: { vendor_po_id?: number; vendor_po_number?: string }, report: HTMLElement): Promise<void> {
  const tab = window.open("", "_blank");
  try {
    const r = await api.tool<{ pdf?: string; error?: string }>("vendor_po_pdf", args);
    if (!r.pdf) throw new Error(r.error ?? "no PDF");
    if (tab) {
      tab.opener = null;
      tab.location.href = r.pdf;
    } else {
      replace(report, h("a", { href: r.pdf, target: "_blank", rel: "noopener" }, "Open the PDF (link valid 15 minutes)"));
    }
  } catch (err) {
    tab?.close();
    replace(report, h("p", { class: "error" }, `The PDF couldn't be opened: ${plain((err as ApiError).message)}`));
  }
}
